import cn from 'classnames';
import styled from 'styled-components';

import { COLORS } from '@/shared/constants/colors';
import { BaseProps } from '@/shared/types';
import { DropdownMenu, MenuItem } from '@/shared/uikit/DropdownMenu';
import { Icon } from '@/shared/uikit/Icon';
import { Title } from '@/shared/uikit/Title';
import { numbers } from '@/shared/utils/numbers';
import { FileItemInfo } from '@/widgets/modals/containers/UploadFiles/types';

type FileItemProps = BaseProps<{
  fileInfo: FileItemInfo;
  withDivider?: boolean;
  onClick?: (item: FileItemInfo) => void;
  onRemove: (id: string) => void;
  statusText?: string;
}>;

export const FileItem = ({
  fileInfo,
  withDivider = true,
  className,
  style,
  onClick,
  onRemove,
  statusText,
}: FileItemProps) => {
  const actions: Array<MenuItem> = [
    {
      label: 'Delete',
      icon: 'trash',
      color: 'red',
      onClick: () => onRemove(fileInfo.id),
    },
  ];

  return (
    <Container
      onClick={() => onClick?.(fileInfo)}
      className={cn(className, { withDivider })}
      style={style}
    >
      <FileIcon>
        <Icon name="file-text" size="s20" />
      </FileIcon>
      <Info>
        <Title size={14} bold className="truncate">
          {fileInfo.file.name}
        </Title>
        <SubInfo>
          <FileSize>{numbers.formatMemoryFromBytes(fileInfo.file.size)}</FileSize>
          {statusText && (
            <SubInfo>
              <div>•</div>
              <StatusText>{statusText}</StatusText>
            </SubInfo>
          )}
        </SubInfo>
      </Info>
      <DropdownMenu items={actions}>
        <ActionButton type="button">
          <Icon name="dots-three" size="s20" />
        </ActionButton>
      </DropdownMenu>
    </Container>
  );
};

const Container = styled.div`
  min-height: 56px;
  display: grid;
  grid-template-columns: 30px 1fr 24px;
  gap: 12px;
  align-items: start;
  padding: 10px 0;

  &.withDivider {
    border-bottom: 1px solid ${COLORS.systemLight300};
  }
`;

const FileIcon = styled.div`
  height: 40px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${COLORS.systemLight100};
  background: ${COLORS.systemDark200};
`;

const Info = styled.div`
  min-width: 0;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  height: 100%;
`;

const SubInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: ${COLORS.systemDark200};
`;

const StatusText = styled.span`
  font-size: 12px;
  line-height: 20px;
  color: ${COLORS.systemForest200};
`;

const FileSize = styled.span`
  font-size: 12px;
  line-height: 20px;
  color: ${COLORS.systemDark200};
`;

const ActionButton = styled.button`
  border: none;
  padding: 0;
  margin: 0;
  background: transparent;
  color: ${COLORS.systemDark200};
  height: 24px;
  width: 24px;
  cursor: pointer;
`;
